import React, { useState } from 'react';
import { useCombobox } from 'downshift';
import classNames from 'classnames';
import { DropdownButton, FormControl, MenuItem } from 'react-bootstrap';

const EmployeesCombobox = ({
  disabled,
  employees,
  selectedEmployeeIds,
  handleSelect,
}) => {
  const selectedCount = selectedEmployeeIds.length;

  const [inputItems, setInputItems] = useState([{}]);

  const searchEmployees = (inputValue, limit = 100) => {
    const inputLowerCase = inputValue.toLowerCase();
    const result = [];

    for (const employee of employees) {
      if (employee.fullName.toLowerCase().includes(inputLowerCase)) {
        result.push(employee);
      }
      if (result.length === limit) {
        break;
      }
    }

    return result;
  };

  const {
    getComboboxProps,
    getInputProps,
    getItemProps,
    getMenuProps,
    getToggleButtonProps,
    isOpen,
  } = useCombobox({
    id: 'employees-filter',
    items: inputItems,
    itemToString: (employee) => (employee ? employee.fullName : ''),
    onInputValueChange: ({ inputValue }) => {
      if (inputValue.length > 2) {
        const employees = searchEmployees(inputValue);
        if (employees.length) {
          setInputItems(employees);
        } else setInputItems([{ id: 'no_result', fullName: 'No result' }]);
      } else setInputItems([{}]);
    },
    onSelectedItemChange: ({ selectedItem }) => handleSelect(selectedItem),
    selectedItem: null,
    stateReducer: (state, actionAndChanges) => {
      const { changes, type } = actionAndChanges;
      switch (type) {
        case useCombobox.stateChangeTypes.InputKeyDownEnter:
        case useCombobox.stateChangeTypes.ItemClick:
          return {
            ...changes,
            // isOpen: true, // Keep menu open after selection
            highlightedIndex: state.highlightedIndex,
            inputValue: '', // Don't add the item string as input value at selection
          };
        case useCombobox.stateChangeTypes.InputBlur:
          return {
            ...changes,
            inputValue: '',
          };
        default:
          return changes;
      }
    },
  });

  return (
    <div className={classNames('dropdown', { open: isOpen })}>
      <div className="input-group" {...getComboboxProps()}>
        <FormControl
          placeholder="Search for employee"
          {...getInputProps({ disabled, refKey: 'inputRef' })}
        />
        <span className="input-group-btn">
          <div className="btn-group btn-group-info">
            <DropdownButton
              bsStyle="info"
              id="employees"
              disabled={!selectedEmployeeIds.length}
              title={selectedCount ? `${selectedCount} Selected` : 'All'}
              className="btn-block text-capitalize dropdown-toggle btn btn-info btn-no-left-border"
              onSelect={handleSelect}
            >
              {employees
                .filter(({ id }) => selectedEmployeeIds.includes(id))
                .map((e) => (
                  <MenuItem key={e.id} active eventKey={e}>
                    <span className="text-capitalize">{e.fullName}</span>
                  </MenuItem>
                ))}
            </DropdownButton>
          </div>
        </span>
      </div>
      <ul className="dropdown-menu" {...getMenuProps()}>
        {inputItems.map((item, index) => {
          if (!item.id) {
            return (
              <React.Fragment key="all">
                <ComboboxItem
                  active={selectedCount === 0}
                  getItemProps={getItemProps}
                  item={item}
                  index={index}
                >
                  All
                </ComboboxItem>
                <li className="divider" role="separator" />
              </React.Fragment>
            );
          }
          return (
            <ComboboxItem
              active={selectedEmployeeIds.includes(item.id)}
              getItemProps={getItemProps}
              item={item}
              index={index}
              key={item.id}
              disabled={item.id === 'no_result'}
            >
              {item.fullName}
            </ComboboxItem>
          );
        })}
      </ul>
    </div>
  );
};

function ComboboxItem({
  active,
  children,
  getItemProps,
  item,
  index,
  disabled,
  ...props
}) {
  return (
    <li className={classNames({ active, disabled })} {...props}>
      <a
        href="#"
        {...getItemProps({
          item,
          index,
          onClick: (event) => event.preventDefault(),
        })}
      >
        {children}
      </a>
    </li>
  );
}

EmployeesCombobox.defaultProps = {
  disabled: false,
  employees: [],
  selectedEmployeeIds: [],
};

export default EmployeesCombobox;
